import React, { useState, useEffect, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import { FaLock, FaMoon, FaSun, FaPaperPlane, FaUsers, FaShieldAlt, FaSignOutAlt, FaPlay, FaExclamationCircle, FaEye, FaEyeSlash, FaCopy, FaCheck, FaSpinner, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import './App.css';
import { JPAKEProtocol } from './jpake.js';
import { hashPassword, calculateMessageSize } from './utils/crypto.js';
import { getRelativeTime } from './utils/time.js';
import { validateRoomId, validateUsername } from './utils/validation.js';
import SecurityTooltip from './components/SecurityTooltip.js';

function App() {
  const [username, setUsername] = useState('');
  const [roomId, setRoomId] = useState('');
  const [password, setPassword] = useState('');
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [joined, setJoined] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [jpake, setJpake] = useState(null);
  const [keyEstablished, setKeyEstablished] = useState(false);
  const [protocolState, setProtocolState] = useState('waiting');
  const [logs, setLogs] = useState([]);
  const [theme, setTheme] = useState('light');
  const [showSecurityTooltip, setShowSecurityTooltip] = useState(false);
  const [formError, setFormError] = useState('');
  const [messageError, setMessageError] = useState('');
  const encoder = new TextEncoder();
  const kcTimeoutRef = useRef(null);

  // New UI/UX state
  const [showPassword, setShowPassword] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [copiedRoomId, setCopiedRoomId] = useState(false);
  const [userScrolledUp, setUserScrolledUp] = useState(false);
  const [messageSizePercent, setMessageSizePercent] = useState(0);
  const [lastMessageSent, setLastMessageSent] = useState(null);
  const [usernameValidation, setUsernameValidation] = useState({ valid: null, message: '' });
  const [roomIdValidation, setRoomIdValidation] = useState({ valid: null, message: '' });
  const [messageCounters, setMessageCounters] = useState({ sent: 0, received: 0 });
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Store server URL for security tooltip
  const [serverUrl, setServerUrl] = useState('');

  // Detect system theme preference
  const getSystemTheme = useCallback(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }, []);

  // Apply theme changes to DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const initialTheme = savedTheme || getSystemTheme();
    setTheme(initialTheme);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = (e) => {
      // Only update if user hasn't set a manual preference
      if (!localStorage.getItem('theme')) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleThemeChange);
      return () => mediaQuery.removeEventListener('change', handleThemeChange);
    }
    // Legacy browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleThemeChange);
      return () => mediaQuery.removeListener(handleThemeChange);
    }
  }, [getSystemTheme]);

  // Auto-scroll messages to bottom when new messages arrive
  useEffect(() => {
    if (!userScrolledUp && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, userScrolledUp]);

  // Detect user scrolling up
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setUserScrolledUp(!isNearBottom);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Calculate message size percentage
  useEffect(() => {
    if (inputMessage) {
      const size = calculateMessageSize(inputMessage);
      const percent = (size / 48000) * 100;
      setMessageSizePercent(percent);
    } else {
      setMessageSizePercent(0);
    }
  }, [inputMessage]);

  // Clear last message sent indicator after 2 seconds
  useEffect(() => {
    if (lastMessageSent) {
      const timer = setTimeout(() => setLastMessageSent(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [lastMessageSent]);

  // Clear copied room ID indicator after 2 seconds
  useEffect(() => {
    if (copiedRoomId) {
      const timer = setTimeout(() => setCopiedRoomId(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedRoomId]);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    // Save manual preference to localStorage
    localStorage.setItem('theme', newTheme);
  };

  const copyRoomId = () => {
    navigator.clipboard.writeText(roomId).then(() => {
      setCopiedRoomId(true);
      addLog('Room ID copied to clipboard', 'success');
    }).catch(() => {
      addLog('Failed to copy room ID', 'error');
    });
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setUserScrolledUp(false);
    }
  };

  /**
   * Add a log entry for protocol events and debugging.
   */
  const addLog = useCallback((message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[${type}] ${message}`);
  }, []);

  /**
   * Handle room join: connect to server, hash password, setup socket listeners.
   */
  const handleJoin = async () => {
    if (!username || !roomId || !password) {
      addLog('Please fill in all fields', 'error');
      setFormError('Please fill in all fields');
      return;
    }

    setIsJoining(true);
    addLog('Securing password...', 'info');
    const passwordHash = await hashPassword(password, roomId);
    // Clear password from memory as soon as possible
    setPassword('');

    addLog('Connecting to server...', 'info');
    const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3001';
    setServerUrl(SERVER_URL);
    const newSocket = io(SERVER_URL, { transports: ['websocket', 'polling'] });

    let jpakeRef = null;

    newSocket.on('connect', () => {
      addLog('Connected to server', 'success');
      setConnected(true);
      setIsJoining(false);
      newSocket.emit('join_room', { roomId, username });
    });

    newSocket.on('disconnect', () => {
      addLog('Disconnected from server', 'error');
      setConnected(false);
      setIsJoining(false);
    });

    newSocket.on('room_joined', (data) => {
      addLog(`Joined room with ${data.participantCount} participant(s)`, 'success');
      setJoined(true);
      setParticipants(data.participants || []);
    });

    newSocket.on('participant_joined', (data) => {
      addLog(`${data.username} joined the room`, 'info');
      setParticipants(data.participants || []);
    });

    newSocket.on('participant_left', (data) => {
      addLog(`${data.username} left the room`, 'warning');
      setParticipants(data.participants || []);
      setKeyEstablished(false);
      setProtocolState('waiting');
    });

    newSocket.on('room_full', () => {
      addLog('Room is full (2 participants max).', 'error');
      setFormError('Room is full (2 participants max)');
      setIsJoining(false);
      newSocket.disconnect();
    });

    newSocket.on('ready_for_protocol', (data) => {
      addLog('Both parties present. Starting J-PAKE protocol...', 'success');
      setProtocolState('ready');

      // Start performance timing (if perfCollector is available for testing)
      if (window.perfCollector) {
        window.perfCollector.startTimer('handshake_total');
        window.perfCollector.resetCounter('exponentiations');
      }

      // Note: isInitiator and peerName data available in data object if needed
      const sortedUsers = (data.participants || [username]).slice().sort();

      const ctxStr = `lockstep/v1|sid:${data.sessionId}|room:${roomId}|users:${sortedUsers.join(',')}`;
      const contextBytes = encoder.encode(ctxStr);
      const isAlice = username === sortedUsers[0];

      const jpakeInstance = new JPAKEProtocol(passwordHash, contextBytes, isAlice, sortedUsers[0], sortedUsers[1]);
      setJpake(jpakeInstance);
      jpakeRef = jpakeInstance;

      if (data.isInitiator) {
        addLog('You are the protocol initiator (joined first)', 'info');
        setTimeout(async () => {
          try {
            await startJPAKE(newSocket, jpakeInstance);
          } catch (e) {
            addLog(`Protocol start failed: ${e.message}`, 'error');
          }
        }, 300);
      } else {
        addLog('Waiting for protocol initiation from peer (they joined first)', 'info');
      }
    });

    newSocket.on('jpake_round1', async (data) => {
      addLog('Received Round 1 data from peer', 'info');
      await handleRound1(newSocket, data, jpakeRef || jpake);
    });

    newSocket.on('jpake_round2', async (data) => {
      addLog('Received Round 2 data from peer', 'info');
      await handleRound2(newSocket, data, jpakeRef || jpake);
    });

    // Key confirmation relay
    newSocket.on('kc1', async ({ tag }) => {
      try {
        // Peer is Alice for KC1
        const ok = await (jpakeRef || jpake).kcVerifyKC1U(true, tag);
        if (!ok) throw new Error('KC1 tag mismatch - incorrect password?');
        addLog('Key confirmation (KC_1_U) verified. Sending KC2...', 'success');
        const tag2 = await (jpakeRef || jpake).kcTagKC1U(false); // Send Bob tag as KC2
        newSocket.emit('kc2', { tag: tag2 });
        addLog('Session key established!', 'success');
        if (kcTimeoutRef.current) clearTimeout(kcTimeoutRef.current);

        // Stop performance timing (Bob's perspective - key confirmation complete)
        if (window.perfCollector) {
          window.perfCollector.stopTimer('handshake_total');
        }

        setKeyEstablished(true);
        setProtocolState('complete');
      } catch (err) {
        addLog(`Key confirmation failed: ${err.message}`, 'error');
        if (kcTimeoutRef.current) clearTimeout(kcTimeoutRef.current);
        setFormError('Key confirmation failed - incorrect password?');
        setTimeout(() => handleDisconnect(), 2000);
      }
    });

    newSocket.on('kc2', async ({ tag }) => {
      try {
        // Peer is Bob for KC2
        const ok = await (jpakeRef || jpake).kcVerifyKC1U(false, tag);
        if (!ok) throw new Error('KC2 tag mismatch - incorrect password?');
        addLog('Key confirmation (KC_1_U) verified. Session established!', 'success');
        if (kcTimeoutRef.current) clearTimeout(kcTimeoutRef.current);

        // Stop performance timing (Alice's perspective - key confirmation complete)
        if (window.perfCollector) {
          window.perfCollector.stopTimer('handshake_total');
        }

        setKeyEstablished(true);
        setProtocolState('complete');
      } catch (err) {
        addLog(`Key confirmation failed: ${err.message}`, 'error');
        if (kcTimeoutRef.current) clearTimeout(kcTimeoutRef.current);
        setFormError('Key confirmation failed - incorrect password?');
        setTimeout(() => handleDisconnect(), 2000);
      }
    });

    newSocket.on('secure_message', async (data) => {
      const jpakeToUse = jpakeRef || jpake;
      if (jpakeToUse && jpakeToUse.sessionKey) {
        try {
          const decrypted = await jpakeToUse.decrypt(data.message, data.ctr);
          setMessages(prev => [...prev, {
            sender: data.sender,
            content: decrypted,
            timestamp: new Date(),
            isSelf: false
          }]);
          setMessageCounters(prev => ({ ...prev, received: prev.received + 1 }));
        } catch (err) {
          addLog(`Failed to decrypt message: ${err.message}`, 'error');
        }
      } else {
        addLog('Cannot decrypt: Session key not established yet', 'warning');
      }
    });

    newSocket.on('error', (error) => {
      const errorMsg = error?.message || error || 'Unknown error';
      addLog(`Socket error: ${errorMsg}`, 'error');

      // Show user-friendly error in form if not yet joined
      if (!joined) {
        setFormError(errorMsg);
        setIsJoining(false);
      }
    });

    setSocket(newSocket);
  };

  /**
   * Initiate the J-PAKE protocol by generating and sending Round 1 data.
   * @param {Socket} sock - Socket.IO socket instance
   * @param {JPAKEProtocol} jpakeInstance - J-PAKE protocol instance
   */
  const startJPAKE = async (sock, jpakeInstance) => {
    try {
      addLog('Generating Round 1 data...', 'info');
      const round1Data = await jpakeInstance.generateRound1();
      addLog('Sending Round 1 data to peer', 'info');
      sock.emit('jpake_round1', round1Data);
      setProtocolState('round1_sent');
    } catch (err) {
      addLog(`Round 1 generation failed: ${err.message}`, 'error');
    }
  };

  /**
   * Handle incoming Round 1 data from peer, verify ZKPs, and proceed to Round 2.
   * Alice initiates Round 2 immediately; Bob waits for Alice's Round 2.
   * @param {Socket} sock - Socket.IO socket instance
   * @param {Object} data - Peer's Round 1 data {g1, g2, zkp1, zkp2}
   * @param {JPAKEProtocol} jpakeInstance - J-PAKE protocol instance
   */
  const handleRound1 = async (sock, data, jpakeInstance) => {
    const jpakeToUse = jpakeInstance || jpake;
    if (!jpakeToUse) {
      addLog('J-PAKE not initialized', 'error');
      return;
    }

    try {
      if (!jpakeToUse.g1 || !jpakeToUse.g2) {
        addLog('Generating own Round 1 data first...', 'info');
        const round1Data = await jpakeToUse.generateRound1();
        addLog('Sending Round 1 data to peer', 'info');
        sock.emit('jpake_round1', round1Data);
        setProtocolState('round1_sent');
      }

      addLog('Processing peer Round 1 data...', 'info');
      await jpakeToUse.processRound1(data);
      addLog('Round 1 data verified successfully', 'success');

      // Deterministic ordering to avoid race: Alice sends Round 2 first; Bob waits
      if ((jpakeToUse).isAlice) {
        addLog('Generating Round 2 data...', 'info');
        const round2Data = await jpakeToUse.generateRound2();
        addLog('Sending Round 2 data to peer', 'info');
        sock.emit('jpake_round2', round2Data);
        setProtocolState('round2_sent');
      } else {
        addLog('Waiting for Alice to send Round 2...', 'info');
        setProtocolState('round2_wait');
      }
    } catch (err) {
      addLog(`Round 1 processing failed: ${err.message}`, 'error');
    }
  };

  /**
   * Handle incoming Round 2 data from peer, derive session keys, and initiate key confirmation.
   * Alice sends KC_1_U first; Bob waits for Alice's KC_1_U.
   * @param {Socket} sock - Socket.IO socket instance
   * @param {Object} data - Peer's Round 2 data {A, zkpA}
   * @param {JPAKEProtocol} jpakeInstance - J-PAKE protocol instance
   */
  const handleRound2 = async (sock, data, jpakeInstance) => {
    const jpakeToUse = jpakeInstance || jpake;
    if (!jpakeToUse) {
      addLog('J-PAKE not initialized', 'error');
      return;
    }

    try {
      // If we haven't generated our Round 2 yet (Bob's side), do it now and send
      if (jpakeToUse.state === 'round1_processed') {
        addLog('Generating Round 2 data...', 'info');
        const round2Data = await jpakeToUse.generateRound2();
        addLog('Sending Round 2 data to peer', 'info');
        sock.emit('jpake_round2', round2Data);
        setProtocolState('round2_sent');
      }

      await jpakeToUse.processRound2(data);
      addLog('Round 2 data verified successfully', 'success');
      addLog('Deriving session keys...', 'info');

      // Start KC timeout (30 seconds)
      if (kcTimeoutRef.current) clearTimeout(kcTimeoutRef.current);
      kcTimeoutRef.current = setTimeout(() => {
        if (!keyEstablished) {
          addLog('Key confirmation timeout - incorrect password?', 'error');
          setFormError('Key confirmation timeout - incorrect password?');
          setTimeout(() => handleDisconnect(), 2000);
        }
      }, 30000);

      // Use deterministic role to avoid deadlock: Alice sends KC1 first
      if ((jpakeToUse).isAlice) {
        addLog('Sending key confirmation (KC_1_U)...', 'info');
        const tag1 = await (jpakeToUse).kcTagKC1U(true); // Alice sends Alice tag as KC1
        sock.emit('kc1', { tag: tag1 });
        setProtocolState('kc1_sent');
      } else {
        setProtocolState('kc_wait');
      }
    } catch (err) {
      addLog(`Round 2 processing failed: ${err.message}`, 'error');
    }
  };

  /**
   * Encrypt and send a message to the peer using AES-256-GCM.
   * Validates message size (48KB limit) before encryption.
   */
  const sendMessage = async () => {
    if (!inputMessage || !jpake || !keyEstablished) {
      console.error('sendMessage prerequisites:', { inputMessage, jpake: !!jpake, keyEstablished });
      return;
    }

    // Validate message size (50KB limit to match server)
    const messageSize = new TextEncoder().encode(inputMessage).length;
    const MAX_MESSAGE_SIZE = 48000; // Slightly under 50KB to account for encryption overhead
    if (messageSize > MAX_MESSAGE_SIZE) {
      const sizeKB = (messageSize / 1024).toFixed(1);
      const maxKB = (MAX_MESSAGE_SIZE / 1024).toFixed(1);
      setMessageError(`Message too large (${sizeKB} KB / ${maxKB} KB max)`);
      return;
    }

    try {
      setMessageError(''); // Clear any previous errors
      const { b64, ctr } = await jpake.encrypt(inputMessage);
      socket.emit('secure_message', { message: b64, ctr });
      const now = new Date();
      setMessages(prev => [...prev, {
        sender: username,
        content: inputMessage,
        timestamp: now,
        isSelf: true
      }]);
      setMessageCounters(prev => ({ ...prev, sent: prev.sent + 1 }));
      setLastMessageSent(now);
      setInputMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
      addLog(`Failed to send message: ${err.message}`, 'error');
      setMessageError(`Failed to send: ${err.message}`);
    }
  };

  const confirmLeave = () => {
    setShowLeaveConfirm(true);
  };

  /**
   * Disconnect from the server and clean up all session state.
   * Securely clears cryptographic keys from memory.
   */
  const handleDisconnect = () => {
    setShowLeaveConfirm(false);

    if (socket) {
      socket.disconnect();
      setSocket(null);
    }

    if (jpake) {
      jpake.cleanup();
    }

    // Clear KC timeout if exists
    if (kcTimeoutRef.current) {
      clearTimeout(kcTimeoutRef.current);
      kcTimeoutRef.current = null;
    }

    setConnected(false);
    setJoined(false);
    setKeyEstablished(false);
    setJpake(null);
    setProtocolState('waiting');
    setMessages([]);
    setLogs([]);
    setPassword('');
    setFormError('');
    setMessageError('');
    setIsJoining(false);
    setMessageCounters({ sent: 0, received: 0 });
  };

  return (
    <div className="app-container">
      <header className="app-header">
        <div className="app-title"><FaLock style={{ marginRight: '0.5rem' }} />Lockstep Chat</div>
        <button className="theme-toggle" onClick={toggleTheme}>{theme === 'light' ? <FaMoon /> : <FaSun />}</button>
      </header>
      <div className="main-content">
        {!joined ? (
          <div className="setup-container">
            <h2 className="setup-title">Join Secure Room</h2>
            <p className="setup-subtitle">Enter credentials to establish encrypted communication</p>
            {formError && (
              <div className="form-error">
                <FaExclamationCircle className="form-error-icon" />
                <span>{formError}</span>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Username</label>
              <div className="input-with-validation">
                <input
                  className={`form-input ${usernameValidation.valid === false ? 'invalid' : usernameValidation.valid === true ? 'valid' : ''}`}
                  type="text"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setFormError('');
                    setUsernameValidation(validateUsername(e.target.value));
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                  placeholder="Enter your username"
                />
                {usernameValidation.valid === true && <FaCheckCircle className="validation-icon valid" />}
                {usernameValidation.valid === false && <FaTimesCircle className="validation-icon invalid" />}
              </div>
              {usernameValidation.message && (
                <div className={`validation-message ${usernameValidation.valid ? 'valid' : 'invalid'}`}>
                  {usernameValidation.message}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Room ID</label>
              <div className="input-with-validation">
                <input
                  className={`form-input ${roomIdValidation.valid === false ? 'invalid' : roomIdValidation.valid === true ? 'valid' : ''}`}
                  type="text"
                  value={roomId}
                  onChange={(e) => {
                    setRoomId(e.target.value);
                    setFormError('');
                    setRoomIdValidation(validateRoomId(e.target.value));
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                  placeholder="Enter room ID"
                />
                {roomIdValidation.valid === true && <FaCheckCircle className="validation-icon valid" />}
                {roomIdValidation.valid === false && <FaTimesCircle className="validation-icon invalid" />}
              </div>
              {roomIdValidation.message && (
                <div className={`validation-message ${roomIdValidation.valid ? 'valid' : 'invalid'}`}>
                  {roomIdValidation.message}
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Shared Password</label>
              <div className="password-input-wrapper">
                <input
                  className="form-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setFormError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
                  placeholder="Enter shared password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            <button
              onClick={handleJoin}
              className="btn btn-primary"
              disabled={isJoining}
            >
              {isJoining ? (
                <><FaSpinner className="spinner" style={{ marginRight: '0.5rem', fontSize: '0.875rem' }} />Connecting...</>
              ) : (
                <><FaPlay style={{ marginRight: '0.5rem', fontSize: '0.875rem' }} />Join Room</>
              )}
            </button>
            <div className="keyboard-hint">
              <small>Press Enter in any field to join</small>
            </div>
          </div>
        ) : keyEstablished ? (
          <div className="chat-container">
            <div className="chat-header">
              <div className="chat-info">
                <div className="chat-room">
                  <span>Room: {roomId}</span>
                  <button
                    className="copy-room-btn"
                    onClick={copyRoomId}
                    title="Copy room ID"
                  >
                    {copiedRoomId ? <FaCheck /> : <FaCopy />}
                  </button>
                </div>
                <div className="chat-status"><FaUsers style={{ marginRight: '0.25rem', fontSize: '0.875rem' }} /><span>{participants.join(', ')}</span></div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <button
                  className="icon-btn encryption-indicator"
                  title="Click for security details"
                  onClick={() => setShowSecurityTooltip(true)}
                >
                  <FaShieldAlt />
                </button>
                <button onClick={confirmLeave} className="icon-btn btn-danger" title="Disconnect"><FaSignOutAlt /></button>
              </div>
            </div>
            <div className="messages-container" ref={messagesContainerRef}>
              {messages.length === 0 ? (
                <div className="empty-chat-state">
                  <FaLock className="empty-icon" />
                  <h3>Secure Chat Ready</h3>
                  <p>Your end-to-end encrypted connection is established.</p>
                  <p>Start the conversation!</p>
                </div>
              ) : (
                <>
                  {messages.map((msg, idx) => {
                    const isLastSent = lastMessageSent && msg.timestamp === lastMessageSent && msg.isSelf;
                    return (
                      <div key={idx} className={`message ${msg.isSelf ? 'own' : ''}`}>
                        <div className="message-content">
                          <div className="message-header">
                            <span className="message-username">{msg.sender}</span>
                            <span className="message-time">{getRelativeTime(msg.timestamp)}</span>
                            {isLastSent && <FaCheck className="sent-indicator" />}
                          </div>
                          <div className="message-bubble">{msg.content}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
              {userScrolledUp && (
                <button className="scroll-to-bottom" onClick={scrollToBottom} title="New messages">
                  <span>↓ New messages</span>
                </button>
              )}
            </div>
            {messageError && (
              <div className="message-error">
                <FaExclamationCircle className="message-error-icon" />
                <span>{messageError}</span>
              </div>
            )}
            <div className="message-input-container">
              <div className="message-input-wrapper">
                <input
                  className="message-input"
                  type="text"
                  value={inputMessage}
                  onChange={(e) => {
                    setInputMessage(e.target.value);
                    setMessageError('');
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Type encrypted message... (Enter to send)"
                />
                {inputMessage && (
                  <div className={`message-size-indicator ${messageSizePercent > 80 ? 'warning' : ''}`}>
                    {(calculateMessageSize(inputMessage) / 1024).toFixed(1)} KB / 46.9 KB
                    {messageSizePercent > 80 && ' ⚠️'}
                  </div>
                )}
              </div>
              <button
                onClick={sendMessage}
                className="send-button"
                disabled={!inputMessage || messageSizePercent >= 100}
                aria-label="Send message"
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        ) : (
          <div className="protocol-container">
            <h2 className="protocol-title">J-PAKE Protocol Status</h2>
            <div className="status-card"><div className="status-label">Connection Status</div><div className="status-value"><span className={`status-indicator ${connected ? 'ready' : 'waiting'}`}></span>{connected ? 'Connected' : 'Disconnected'}</div></div>
            <div className="status-card"><div className="status-label">Room</div><div className="status-value">{roomId}</div></div>
            <div className="status-card"><div className="status-label">Participants</div><div className="status-value">{participants.join(', ') || 'Waiting...'}</div></div>
            <div className="status-card"><div className="status-label">Protocol State</div><div className="status-value"><span className={`status-indicator ${protocolState}`}></span>
              {protocolState === 'waiting' && 'Waiting for second participant...'}
              {protocolState === 'ready' && 'Starting secure handshake...'}
              {protocolState === 'round1_sent' && 'Exchanging encryption parameters...'}
              {protocolState === 'round2_sent' && 'Deriving shared encryption key...'}
              {protocolState === 'kc1_sent' && 'Confirming key agreement...'}
              {protocolState === 'kc_wait' && 'Verifying encryption setup...'}
              {protocolState === 'complete' && 'Secure connection established!'}
            </div></div>
            <div className="protocol-log">{logs.slice(-10).map((log, idx) => (<div key={idx} className="log-entry">[{log.timestamp}] {log.message}</div>))}</div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}><button onClick={confirmLeave} className="icon-btn btn-danger" title="Leave Room"><FaSignOutAlt /></button></div>
          </div>
        )}
      </div>
      
      {/* Security Tooltip */}
      <SecurityTooltip
        visible={showSecurityTooltip}
        serverUrl={serverUrl}
        onClose={() => setShowSecurityTooltip(false)}
        connected={connected}
        protocolState={protocolState}
        roomId={roomId}
        participants={participants}
        keyEstablished={keyEstablished}
        messageCounters={messageCounters}
      />

      {/* Leave Confirmation Dialog */}
      {showLeaveConfirm && (
        <div className="modal-overlay" onClick={() => setShowLeaveConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Leave Room?</h3>
            <p>Are you sure you want to disconnect? This will end your secure session.</p>
            <div className="modal-actions">
              <button onClick={() => setShowLeaveConfirm(false)} className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleDisconnect} className="btn btn-danger">
                Leave Room
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
